import React, { useState, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Collapse,
    Chip,
    TextField,
    InputAdornment,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Slider,
    makeStyles
} from '@material-ui/core';
import { CheckCircleIcon, ErrorIcon, TimesIcon, ChevronDownIcon } from '../../icons';
import { useNotify } from '../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../hooks/useSwaggerClient';
import { useReduxState } from '../../hooks/useReduxState';
import { cancelReasonAutocomplete } from '../../autocomplete';
import AutocompleteSelect from '../atom/AutocompleteSelect';
import { formatMoney } from '../../utils';
import moment from 'moment';
import postActiviterMessage from '../../utils/postActiviterMessage';

const useStyles = makeStyles(theme => ({
    paper: { borderRadius: 14 },
    header: {
        padding: '16px 20px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    title: { fontSize: 17, fontWeight: 700, color: theme.palette.text.primary },
    action: { fontSize: 14, marginLeft: 8, fontWeight: 700 },
    actionRefund: { color: theme.palette.primary.main },
    actionCancel: { color: theme.palette.error.main },
    tabs: {
        display: 'flex',
        justifyContent: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: '0 20px',
        marginTop: 12
    },
    tab: {
        padding: '10px 16px 11px',
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        border: 0,
        background: 'transparent',
        position: 'relative',
        fontFamily: 'inherit',
        '&:hover': { color: theme.palette.text.primary }
    },
    tabActive: {
        color: theme.palette.text.primary,
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -1,
            height: 2,
            background: theme.palette.text.primary
        }
    },
    tabDangerActive: {
        color: theme.palette.error.main,
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -1,
            height: 2,
            background: theme.palette.error.main
        }
    },
    body: { padding: '16px 20px' },
    section: { marginBottom: 16 },
    sectionDivided: {
        marginBottom: 16,
        paddingTop: 16,
        borderTop: `1px solid ${theme.palette.divider}`
    },
    fieldLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.primary,
        marginBottom: 8
    },
    fieldHint: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 8,
        lineHeight: 1.5
    },
    seg: {
        display: 'inline-flex',
        border: `1px solid ${theme.palette.divider}`,
        padding: 3,
        borderRadius: 8,
        background: theme.palette.background.paper
    },
    segFull: { display: 'flex', width: '100%' },
    segBtn: {
        border: 0,
        background: 'transparent',
        padding: '6px 12px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        fontFamily: 'inherit',
        flex: 'none',
        '&:hover': { color: theme.palette.text.primary }
    },
    segBtnFull: { flex: 1 },
    segBtnActive: {
        background: theme.palette.text.primary,
        color: theme.palette.background.paper,
        '&:hover': { color: theme.palette.background.paper }
    },
    segBtnDangerActive: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': { color: theme.palette.error.contrastText }
    },
    chipsRow: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
    optionGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
    option: {
        padding: '10px 12px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        background: theme.palette.background.paper,
        cursor: 'pointer',
        transition: 'border-color .15s'
    },
    optionActive: {
        borderColor: theme.palette.primary.main,
        background: theme.palette.action.selected
    },
    optionDisabled: { opacity: 0.5, cursor: 'not-allowed' },
    optionLabel: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    optionDesc: { fontSize: 11, color: theme.palette.text.secondary, marginTop: 2 },
    review: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: '10px 12px',
        marginTop: 8
    },
    reviewHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        userSelect: 'none'
    },
    reviewTitle: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.primary
    },
    chevron: {
        transition: 'transform .2s',
        fontSize: 12,
        color: theme.palette.text.secondary
    },
    chevronOpen: { transform: 'rotate(180deg)' },
    bkRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 0',
        borderBottom: `1px dashed ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 0 }
    },
    bkTitle: { fontSize: 12, color: theme.palette.text.primary, fontWeight: 500 },
    bkMeta: { fontSize: 11, color: theme.palette.text.secondary },
    bkAmt: { fontSize: 13, fontWeight: 700, color: theme.palette.primary.main },
    bkAmtNone: { color: theme.palette.error.main, fontSize: 11, fontWeight: 600 },
    splitLegend: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 8
    },
    resultsBox: {
        maxHeight: 300,
        overflow: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        marginTop: 8
    },
    reviewListTop: {
        marginTop: 8,
        paddingTop: 8,
        borderTop: `1px solid ${theme.palette.divider}`
    }
}));

const SPLIT_VENDOR = 'vendor';
const SPLIT_AGENCY = 'agency';
const SPLIT_CUSTOM = 'custom';
const DEST_ORIGINAL = 'original';
const DEST_BALANCE = 'balance';
const AMT_PCT = 'pct';
const AMT_FIXED = 'fixed';
const REFUND_FULL = 'full';
const REFUND_NONE = 'none';
const TAB_REFUND = 'refund';
const TAB_CANCEL = 'cancel';

function Seg({ options, value, onChange, full = false, classes }) {
    return (
        <div className={`${classes.seg} ${full ? classes.segFull : ''}`}>
            {options.map(opt => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        className={[
                            classes.segBtn,
                            full ? classes.segBtnFull : '',
                            active && opt.danger ? classes.segBtnDangerActive : '',
                            active && !opt.danger ? classes.segBtnActive : ''
                        ].join(' ')}
                        onClick={() => onChange(opt.value)}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function OptionCard({ active, disabled, label, desc, onClick, classes }) {
    return (
        <div
            className={[
                classes.option,
                active ? classes.optionActive : '',
                disabled ? classes.optionDisabled : ''
            ].join(' ')}
            onClick={() => !disabled && onClick()}
        >
            <div className={classes.optionLabel}>{label}</div>
            <div className={classes.optionDesc}>{desc}</div>
        </div>
    );
}

function BulkRefundCancelModal({
    bookingItems,
    initialTab = TAB_CANCEL,
    onFinished,
    showModal,
    MODAL_TYPE
}) {
    const classes = useStyles();
    const { notifySuccess, notifyError } = useNotify();
    const reduxState = useReduxState();
    const role = reduxState.user?.user?.role || 'guest';

    const items = useMemo(() => bookingItems || [], [bookingItems]);
    const totalItems = items.length;
    const totalCharged = useMemo(() => items.reduce((s, it) => s + (it.amount || 0), 0), [items]);

    const [tab, setTab] = useState(initialTab);

    // Refund tab state
    const [amtType, setAmtType] = useState(AMT_PCT);
    const [pct, setPct] = useState(50);
    const [fixedAmt, setFixedAmt] = useState('100.00');
    const [split, setSplit] = useState(SPLIT_AGENCY);
    const [vendorPct, setVendorPct] = useState(50);
    const [dest, setDest] = useState(DEST_ORIGINAL);
    const [note, setNote] = useState('');

    // Cancel tab state
    const [cancelReason, setCancelReason] = useState(null);
    const [cancelReasonDesc, setCancelReasonDesc] = useState('');
    const [cancelNote, setCancelNote] = useState('');
    const [cancelRefund, setCancelRefund] = useState(REFUND_FULL);
    const [cSplit, setCSplit] = useState(SPLIT_AGENCY);
    const [cVendorPct, setCVendorPct] = useState(50);
    const [cDest, setCDest] = useState(DEST_ORIGINAL);

    // Review collapse + execution
    const [reviewOpen, setReviewOpen] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState([]);

    // Balance availability
    const firstUserId = items[0]?.booking?.user?.id;
    const [balancesData] = useSwaggerClient(
        'balance',
        'getBalance',
        firstUserId ? { userId: firstUserId } : null
    );
    const balanceSlug = balancesData?.balances?.[0]?.slug;
    const balanceName = balancesData?.balances?.[0]?.name;
    const hasBalance = !!balanceSlug;

    const parsedFixed = parseFloat(fixedAmt) || 0;

    const refundPerBooking = useMemo(() => {
        if (amtType === AMT_PCT) {
            return items.map(it => +(((it.amount || 0) * pct) / 100).toFixed(2));
        }
        return items.map(it => +Math.min(it.amount || 0, parsedFixed).toFixed(2));
    }, [items, amtType, pct, parsedFixed]);

    const totalRefund = useMemo(() => refundPerBooking.reduce((a, b) => a + b, 0), [
        refundPerBooking
    ]);

    const resolveVendorPct = (s, custom) =>
        s === SPLIT_VENDOR ? 100 : s === SPLIT_AGENCY ? 0 : custom;

    const refundVPct = resolveVendorPct(split, vendorPct);
    const refundVAmt = (totalRefund * refundVPct) / 100;
    const refundAAmt = totalRefund - refundVAmt;

    const cancelRefundTotal = cancelRefund === REFUND_FULL ? totalCharged : 0;
    const cCancelVPct = resolveVendorPct(cSplit, cVendorPct);
    const cancelVAmt = (cancelRefundTotal * cCancelVPct) / 100;
    const cancelAAmt = cancelRefundTotal - cancelVAmt;

    const close = useCallback(() => showModal(MODAL_TYPE.NONE), [showModal, MODAL_TYPE]);

    const getGuestAndActivity = item => ({
        guestName:
            `${item.booking?.user?.firstname || ''} ${item.booking?.user?.lastname || ''}`.trim() ||
            'Unknown Guest',
        activityName: item.activity?.name || 'Unknown Activity'
    });

    const splitAmount = (amount, vPct) => {
        const vendor = Math.round(amount * (vPct / 100) * 100) / 100;
        return { vendor_refund: vendor, agency_refund: +(amount - vendor).toFixed(2) };
    };

    const runRefund = useCallback(async () => {
        setInProgress(true);
        setProgress(0);
        setResults([]);
        const out = [];

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const amount = refundPerBooking[i];
            const { vendor_refund, agency_refund } = splitAmount(amount, refundVPct);
            const { guestName, activityName } = getGuestAndActivity(item);

            try {
                await doSwaggerCall('bookings', 'partialRefundBookingItem', {
                    bookingItemId: item.id,
                    partialRefundData: {
                        agency_refund,
                        vendor_refund,
                        forceToBalance: dest === DEST_BALANCE && hasBalance ? balanceSlug : false,
                        note:
                            note ||
                            (amtType === AMT_PCT
                                ? `Bulk refund ${pct}%`
                                : `Bulk refund ${formatMoney(parsedFixed)}`)
                    }
                });
                out.push({
                    id: item.id,
                    activityName,
                    guestName,
                    status: 'success',
                    message: `Refunded ${formatMoney(amount)}`
                });
                notifySuccess(`Refunded ${formatMoney(amount)} for booking ${item.id}`);
            } catch (err) {
                out.push({
                    id: item.id,
                    activityName,
                    guestName,
                    status: 'error',
                    message: err.response?.body?.message || err.message || 'Failed to refund'
                });
                notifyError(`Failed to refund booking ${item.id}: ${err.toString()}`);
            }
            setProgress(((i + 1) / items.length) * 100);
            setResults([...out]);
        }

        setInProgress(false);
        setCompleted(true);
    }, [
        items,
        refundPerBooking,
        refundVPct,
        dest,
        hasBalance,
        balanceSlug,
        note,
        amtType,
        pct,
        parsedFixed,
        notifySuccess,
        notifyError
    ]);

    const runCancel = useCallback(async () => {
        setInProgress(true);
        setProgress(0);
        setResults([]);
        const out = [];
        const wantsRefund = cancelRefund === REFUND_FULL;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const { guestName, activityName } = getGuestAndActivity(item);
            try {
                await doSwaggerCall('bookings', 'cancelBookingItem', {
                    bookingItemId: item.id,
                    cancelBookingItemData: {
                        forceFullRefund: false,
                        forceNoRefund: true,
                        note: cancelNote,
                        cancelReason
                    }
                });

                let refundMsg = '';
                if (wantsRefund) {
                    const amount = item.amount || 0;
                    const { vendor_refund, agency_refund } = splitAmount(amount, cCancelVPct);
                    try {
                        await doSwaggerCall('bookings', 'partialRefundBookingItem', {
                            bookingItemId: item.id,
                            partialRefundData: {
                                agency_refund,
                                vendor_refund,
                                forceToBalance:
                                    cDest === DEST_BALANCE && hasBalance ? balanceSlug : false,
                                note: cancelNote || 'Bulk cancel — full refund'
                            }
                        });
                        refundMsg = ` · refunded ${formatMoney(amount)}`;
                    } catch (refErr) {
                        out.push({
                            id: item.id,
                            activityName,
                            guestName,
                            status: 'error',
                            message: `Cancelled but refund failed: ${refErr.response?.body
                                ?.message || refErr.message}`
                        });
                        setProgress(((i + 1) / items.length) * 100);
                        setResults([...out]);
                        continue;
                    }
                }

                out.push({
                    id: item.id,
                    activityName,
                    guestName,
                    status: 'success',
                    message: `Cancelled${refundMsg}`
                });
                notifySuccess(`Booking ${item.id} cancelled${refundMsg}`);
            } catch (err) {
                out.push({
                    id: item.id,
                    activityName,
                    guestName,
                    status: 'error',
                    message: err.response?.body?.message || err.message || 'Failed to cancel'
                });
                notifyError(`Failed to cancel booking ${item.id}: ${err.toString()}`);
            }
            setProgress(((i + 1) / items.length) * 100);
            setResults([...out]);
        }

        setInProgress(false);
        setCompleted(true);
        postActiviterMessage({
            type: 'BOOKING_CANCELLED_BULK',
            results: out.map(r => ({
                bookingItemId: r.id,
                status: r.status,
                ...(r.status === 'success' ? { refund: wantsRefund } : { message: r.message })
            }))
        });
    }, [
        items,
        cancelRefund,
        cancelNote,
        cancelReason,
        cCancelVPct,
        cDest,
        hasBalance,
        balanceSlug,
        notifySuccess,
        notifyError
    ]);

    const onPrimary = tab === TAB_REFUND ? runRefund : runCancel;

    const primaryLabel = (() => {
        if (tab === TAB_REFUND) {
            return `Refund ${formatMoney(totalRefund)}`;
        }
        if (cancelRefund === REFUND_FULL) {
            return `Cancel & refund ${formatMoney(cancelRefundTotal)}`;
        }
        return 'Cancel booking';
    })();

    if (!items.length) {
        return null;
    }

    const pctChips = [10, 25, 50, 75, 100];
    const isAgent = ['agent', 'admin'].includes(role);

    const renderSplitSection = (splitVal, setSplitVal, vPctVal, setVPctVal, vAmt, aAmt) => (
        <div className={classes.sectionDivided}>
            <div className={classes.fieldLabel}>Who pays the refund</div>
            <Seg
                classes={classes}
                value={splitVal}
                onChange={setSplitVal}
                options={[
                    { value: SPLIT_VENDOR, label: 'Vendor 100%' },
                    { value: SPLIT_AGENCY, label: 'Agency 100%' },
                    { value: SPLIT_CUSTOM, label: 'Custom' }
                ]}
            />
            {splitVal === SPLIT_CUSTOM && (
                <>
                    <Box mt={1} px={1}>
                        <Slider
                            value={vPctVal}
                            onChange={(e, v) => setVPctVal(v)}
                            min={0}
                            max={100}
                        />
                    </Box>
                    <div className={classes.splitLegend}>
                        <span>
                            Vendor {vPctVal}% · {formatMoney(vAmt)}
                        </span>
                        <span>
                            Agency {100 - vPctVal}% · {formatMoney(aAmt)}
                        </span>
                    </div>
                </>
            )}
        </div>
    );

    const renderDestSection = (destVal, setDestVal) => (
        <div className={classes.sectionDivided}>
            <div className={classes.fieldLabel}>Refund destination</div>
            <div className={classes.optionGrid}>
                <OptionCard
                    classes={classes}
                    active={destVal === DEST_ORIGINAL}
                    label="Original payment"
                    desc="Reverses the charge."
                    onClick={() => setDestVal(DEST_ORIGINAL)}
                />
                <OptionCard
                    classes={classes}
                    active={destVal === DEST_BALANCE}
                    disabled={!hasBalance}
                    label={hasBalance ? balanceName || 'Account balance' : 'Account balance'}
                    desc={hasBalance ? 'Credit for future bookings.' : 'Not available.'}
                    onClick={() => setDestVal(DEST_BALANCE)}
                />
            </div>
        </div>
    );

    const renderReview = (perBookingAmounts, noRefund) => (
        <div className={classes.review}>
            <div className={classes.reviewHead} onClick={() => setReviewOpen(!reviewOpen)}>
                <div className={classes.reviewTitle}>
                    Review ({totalItems} booking{totalItems !== 1 ? 's' : ''})
                </div>
                <ChevronDownIcon
                    className={`${classes.chevron} ${reviewOpen ? classes.chevronOpen : ''}`}
                />
            </div>
            <Collapse in={reviewOpen}>
                <Box className={classes.reviewListTop}>
                    {items.map((item, i) => (
                        <div key={item.id} className={classes.bkRow}>
                            <div>
                                <div className={classes.bkTitle}>
                                    {item.activity?.name || 'Unknown'}
                                </div>
                                <div className={classes.bkMeta}>
                                    {moment.utc(item.activity_start).format('MMM D · h:mm A')}
                                    {' · ID '}
                                    {item.id}
                                </div>
                            </div>
                            {noRefund ? (
                                <span className={classes.bkAmtNone}>No refund</span>
                            ) : (
                                <span className={classes.bkAmt}>
                                    {formatMoney(perBookingAmounts[i])}
                                </span>
                            )}
                        </div>
                    ))}
                </Box>
            </Collapse>
        </div>
    );

    const renderRefundPanel = () => (
        <>
            <div className={classes.section}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <div className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                        Refund amount
                    </div>
                    <Seg
                        classes={classes}
                        value={amtType}
                        onChange={setAmtType}
                        options={[
                            { value: AMT_PCT, label: 'Percentage' },
                            { value: AMT_FIXED, label: 'Fixed amount' }
                        ]}
                    />
                </Box>
                {amtType === AMT_PCT ? (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <div className={classes.chipsRow} style={{ flex: 1 }}>
                            {pctChips.map(v => (
                                <Chip
                                    key={v}
                                    label={`${v}%`}
                                    size="small"
                                    onClick={() => setPct(v)}
                                    color={pct === v ? 'primary' : 'default'}
                                    variant={pct === v ? 'default' : 'outlined'}
                                />
                            ))}
                        </div>
                        <TextField
                            type="number"
                            value={pct}
                            onChange={e => {
                                const v = Math.max(0, Math.min(100, +e.target.value || 0));
                                setPct(v);
                            }}
                            inputProps={{ min: 0, max: 100 }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            size="small"
                            variant="outlined"
                            style={{ width: 100 }}
                        />
                    </Box>
                ) : (
                    <TextField
                        type="number"
                        value={fixedAmt}
                        onChange={e => setFixedAmt(e.target.value)}
                        inputProps={{ min: 0, step: '0.01' }}
                        size="small"
                        variant="outlined"
                        label="Amount per booking ($)"
                        helperText="Capped at each booking's charge"
                        fullWidth
                    />
                )}
            </div>

            {renderSplitSection(split, setSplit, vendorPct, setVendorPct, refundVAmt, refundAAmt)}
            {renderDestSection(dest, setDest)}

            <div className={classes.sectionDivided}>
                <TextField
                    placeholder="Internal note (optional)"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    multiline
                    fullWidth
                    variant="outlined"
                    size="small"
                    minRows={2}
                />
            </div>

            {renderReview(refundPerBooking, false)}
        </>
    );

    const renderCancelPanel = () => (
        <>
            {isAgent && (
                <div className={classes.section}>
                    <div className={classes.fieldLabel} style={{ marginBottom: 14 }}>
                        Cancel reason
                    </div>
                    <AutocompleteSelect
                        initCall={cancelReasonAutocomplete.init}
                        autocompleteCall={cancelReasonAutocomplete.search}
                        value={cancelReason}
                        setValue={(newValue, getById) => {
                            const item = newValue != null ? getById(newValue) : null;
                            setCancelReason(newValue);
                            setCancelReasonDesc(item?.description || '');
                        }}
                    >
                        <TextField label="Cancel reason" variant="outlined" size="small" />
                    </AutocompleteSelect>
                    {cancelReasonDesc && (
                        <Box
                            mt={1}
                            p={1}
                            style={{
                                background: '#f5f5f5',
                                borderLeft: '3px solid #bdbdbd',
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="caption" color="textSecondary">
                                {cancelReasonDesc}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        style={{ marginTop: 12 }}
                        placeholder="Internal note (optional)"
                        value={cancelNote}
                        onChange={e => setCancelNote(e.target.value)}
                        multiline
                        fullWidth
                        variant="outlined"
                        size="small"
                        minRows={2}
                    />
                </div>
            )}

            <div className={classes.sectionDivided}>
                <div className={classes.fieldLabel}>Refund</div>
                <Seg
                    classes={classes}
                    full
                    value={cancelRefund}
                    onChange={setCancelRefund}
                    options={[
                        { value: REFUND_FULL, label: 'Full refund' },
                        { value: REFUND_NONE, label: 'No refund', danger: true }
                    ]}
                />
                <div className={classes.fieldHint}>
                    {cancelRefund === REFUND_FULL
                        ? 'Guest will receive a full refund.'
                        : 'No refund will be issued.'}
                </div>
            </div>

            {cancelRefund === REFUND_FULL &&
                renderSplitSection(
                    cSplit,
                    setCSplit,
                    cVendorPct,
                    setCVendorPct,
                    cancelVAmt,
                    cancelAAmt
                )}
            {cancelRefund === REFUND_FULL && renderDestSection(cDest, setCDest)}

            {renderReview(
                items.map(it => (cancelRefund === REFUND_FULL ? it.amount || 0 : 0)),
                cancelRefund === REFUND_NONE
            )}
        </>
    );

    return (
        <Dialog
            open
            onClose={inProgress ? undefined : close}
            maxWidth="sm"
            fullWidth
            classes={{ paper: classes.paper }}
            aria-labelledby="bulk-refund-cancel-modal"
        >
            <div className={classes.header}>
                <div>
                    <span className={classes.title} id="bulk-refund-cancel-modal">
                        {totalItems} booking{totalItems !== 1 ? 's' : ''}
                    </span>
                    {!inProgress && !completed && (
                        <span
                            className={`${classes.action} ${
                                tab === TAB_CANCEL && cancelRefund === REFUND_NONE
                                    ? classes.actionCancel
                                    : classes.actionRefund
                            }`}
                        >
                            · {primaryLabel}
                        </span>
                    )}
                </div>
                <IconButton size="small" onClick={close} disabled={inProgress}>
                    <TimesIcon />
                </IconButton>
            </div>

            {!inProgress && !completed && (
                <div className={classes.tabs}>
                    <button
                        type="button"
                        className={`${classes.tab} ${tab === TAB_REFUND ? classes.tabActive : ''}`}
                        onClick={() => setTab(TAB_REFUND)}
                    >
                        Refund only
                    </button>
                    <button
                        type="button"
                        className={`${classes.tab} ${
                            tab === TAB_CANCEL ? classes.tabDangerActive : ''
                        }`}
                        onClick={() => setTab(TAB_CANCEL)}
                    >
                        Cancel &amp; refund
                    </button>
                </div>
            )}

            <DialogContent dividers style={{ padding: 0 }}>
                <div className={classes.body}>
                    {inProgress && (
                        <Box mb={2}>
                            <LinearProgress variant="determinate" value={progress} />
                            <Typography variant="caption" color="textSecondary">
                                {Math.round(progress)}% complete
                            </Typography>
                        </Box>
                    )}
                    {results.length > 0 ? (
                        <div className={classes.resultsBox}>
                            <List dense>
                                {results.map((r, i) => (
                                    <React.Fragment key={r.id}>
                                        <ListItem>
                                            <ListItemIcon>
                                                {r.status === 'success' ? (
                                                    <CheckCircleIcon color="success" />
                                                ) : (
                                                    <ErrorIcon color="error" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${r.activityName} — ${r.guestName}`}
                                                secondary={`ID ${r.id} · ${r.message}`}
                                            />
                                        </ListItem>
                                        {i < results.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </div>
                    ) : (
                        !inProgress &&
                        (tab === TAB_REFUND ? renderRefundPanel() : renderCancelPanel())
                    )}
                </div>
            </DialogContent>

            <DialogActions>
                {completed ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (onFinished) {
                                onFinished();
                            }
                            close();
                        }}
                    >
                        Close
                    </Button>
                ) : inProgress ? null : (
                    <>
                        <Button onClick={close}>Cancel</Button>
                        <Button
                            variant="contained"
                            color={tab === TAB_CANCEL ? 'secondary' : 'primary'}
                            onClick={onPrimary}
                            disabled={
                                (tab === TAB_REFUND && totalRefund <= 0) ||
                                (tab === TAB_CANCEL && isAgent && !cancelReason)
                            }
                        >
                            {primaryLabel}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default BulkRefundCancelModal;
